import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Terminal, Menu, X } from "lucide-react";
import { useLang } from "@/i18n";

export const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

function LangToggle({ compact = false }) {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="lang-toggle"
      className={`flex items-center border border-[#DCD6CE] ${compact ? "" : "hidden md:flex"}`}
    >
      {["en", "fr"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
            lang === l ? "bg-neon-teal text-[#FAF8F5]" : "text-[#6B655F] hover:text-[#141210]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Nav() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const links = [
    { id: "about", label: t("nav.about") },
    { id: "services", label: t("nav.tracks") },
    { id: "lab", label: t("nav.lab") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-[#FAF8F5]/85 backdrop-blur-md border-[#E6E2DC]" : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4 md:px-12">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="group flex items-center gap-3"
        >
          <span className="flex h-9 w-9 items-center justify-center border border-neon-teal/60 bg-[#581820]/[0.05] text-neon-teal transition-colors duration-300 group-hover:bg-neon-teal group-hover:text-[#FAF8F5]">
            <Terminal size={18} strokeWidth={2.4} />
          </span>
          <span className="font-display text-lg font-bold uppercase tracking-tight text-[#141210]">
            The Geeks <span className="text-neon-teal">Labs</span>
          </span>
        </button>

        <nav className="hidden items-center gap-10 md:flex" data-testid="nav-links">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="group relative text-xs font-bold uppercase tracking-[0.2em] text-[#6B655F] transition-colors duration-300 hover:text-[#141210]"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-neon-teal transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle />
          <button
            data-testid="nav-cta-button"
            onClick={() => go("contact")}
            className="hidden bg-[#141210] px-6 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:bg-neon-teal md:block"
          >
            {t("nav.cta")}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="text-[#141210] md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="border-t border-[#E6E2DC] bg-[#FAF8F5]/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="py-3 text-left font-display text-2xl font-bold uppercase tracking-tight text-[#141210]"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta"
                onClick={() => go("contact")}
                className="mt-4 bg-neon-teal px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-[#FAF8F5]"
              >
                {t("nav.cta")}
              </button>
              <div className="mt-4">
                <LangToggle compact />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
